import { FormikErrors, FormikTouched } from 'formik';
import { MeasurementFieldConfig } from 'src/models/measurement/measurement';
import { TrainingInfoField, User } from 'src/models/user/_types';
import { ColorHex, Identifier, Timestamp } from 'types/utilityTypes';

import { RoleProps } from '../trainer/_types';

export enum FeatureFlag {
  ArchivedPackages = 'Archived Packages',
  ArchivedServices = 'Archived Services',
  FamilySharing = 'Family Sharing',
  LocationSplitting = 'Location Splitting',
  UsePackageLocation = 'Use Package Location',
  ProductsUseClientLocation = 'Products Use Client Location',
  GoogleCalendarIntegration = 'Google Calendar Integration',
  MergeUsers = 'Merge Users',
  InstantPayouts = 'Instant Payouts',
  Klarna = 'Klarna',
  Classpass = 'Classpass',
  StripeMigration = 'Stripe Migration',
  Quickbooks = 'Quickbooks',
  Mindbody = 'Mindbody',
  IframeGroupTabs = 'Iframe Group Tabs',
  SMS = 'SMS',
  HideOnlineTraining = 'Hide Online Training',
  EnableCaptcha = 'Enable Captcha',
  NoAssessmentRedirect = 'No Assessment Redirect',
  DisableCardValidation = 'Disable Card Validation',
  HideWatermark = 'Hide Powered By Exercise Com Watermark',
  DisableDirectDebitEmails = 'Disable Direct Debit Emails',
  ApiAccess = 'Api Access',
  TimeTracking = 'Time Tracking',
  Webhooks = 'Webhooks',
  EnableNewAppFeatures = 'Enable New App Features',
  RequireExerciseVideos = 'Require Exercise Videos',
  HasCashAndCheck = 'Has Cash and Check',
  DisableBillingInfo = 'Disable Billing Info',
  DisableProfileAddress = 'Disable Profile Address',
  AlternateExerciseVideos = 'Alternate Exercise Videos',
  NewClientsPage = 'New Clients Page',
  NewDashboard = 'New Dashboard',
  WearableIntegration = 'Wearable Integration',
  NewReports = 'New Reports',
  GuidedLogger = 'Guided Logger',
  Onboarding = 'Onboarding',
  KisiAppIntegration = 'Kisi App Integration',
  IntercomAppIntegration = 'Intercom App Integration',
  TapToPayIntegration = 'Tap To Pay Integration',
  RequiredWaiverAppAlerts = 'Required Waiver App Alerts',
  DisableLoggingBodyweight = 'Disable Logging Bodyweight',
  DisableLoggingHeight = 'Disable Logging Height',
  Afterpay = 'Afterpay',
  EnableLocationAndServiceCode = 'Enable Location And Service Code',
  CardTokenization = 'Card Tokenization',
  DisableCashAndCheck = 'Disable Cash and Check',
  ResourceManagement = 'Resource Management',
  StripeTax = 'Stripe Tax',
  StripeTaxSettings = 'Stripe Tax Settings',
  SkipMicrodeposits = 'Skip Microdeposits',
  HideLeaderboardAssessmentResults = 'Hide Leaderboard Assessment Results',
  UseSendingEmailForMessages = 'Use Sending Email For Messages',
  RequireInviteLinkForSiteSignup = 'Require Invite Link For Site Signup',
  PDFWatermarking = 'PDF Watermarking',
  ConnectToStripe = 'Connect to Stripe',
  LegacyStrava = 'Legacy Strava',
}

export interface ProfileMenuItem {
  id?: number;
  name: string;
  title: string;
  url: string;
  icon: string;
  visibility: string[];
  minVersion?: string;
  maxVersion?: string;
  platform?: string;
  groupId?: string;
  public?: boolean;
}

export interface AppDrawerMenuItem {
  id?: number;
  name: string;
  target: string;
  public: boolean;
  visibility: string[];
  minVersion?: string;
  maxVersion?: string;
  icon?: string;
  platform?: string;
  groupId?: string;
}

export interface AppNavigationTab {
  id?: number;
  name: string;
  title: string;
  url: string;
  icon: string;
  visibility: string[];
  minVersion?: string;
  maxVersion?: string;
  platform?: string;
  groupId?: string;
  public?: boolean;
}

export interface LimitBookingService {
  maxVisits: number;
  serviceIds: string[];
  visitsInterval: string;
  visitsIntervalCount: number;
}

export interface AppDashboardCarouselItem {
  name: string;
  description: string;
  title: string;
  imageUrl: string;
  targetUrl: string;
  groupId: string | null;
  minVersion?: string;
  maxVersion?: string;
}

export interface IAPItem {
  type: 'WorkoutPlan' | 'Group' | 'User' | null;
  id: string | null;
  iapId: string | null;
  interval?: number | null;
  intervalType?: string | null;
}

export interface AppLoggerNavItem {
  id: string;
  name: string;
  icon: string;
}

export interface ReferAFriendUrl {
  title?: string;
  url: string;
}

export interface ReferAFriendSettings {
  title?: string;
  subtitle?: string;
  smsText?: string;
  urls?: ReferAFriendUrl[];
}

export interface Platform {
  featureFlags?: FeatureFlag[];
  id: Identifier;
  brand: string;
  subdomain: string;
  host: string;
  subdomainUrl: string;
  alternateHost?: string;
  logoUrl: string;
  darkmodeLogoUrl?: string;
  iconUrl: string;
  twilioPhoneNumber: string;
  smsAvailable?: boolean;
  trainerId: User['id'];
  trainerName: string;
  trainerEmail: string;
  clientTags: string[];
  trainerTags?: string[];
  videoTags?: string[];
  groupResourceTags?: string[];
  groupAssessmentTags?: string[];
  serviceTags?: string[];
  packageTags?: string[];
  productTags?: string[];
  exerciseTags?: string[];
  workoutLabelTags?: { name: string; image?: string }[];
  createdAt: Timestamp;
  updatedAt: Timestamp;
  customExerciseMeasurements: string[];
  disableClientCheckin: boolean;
  cancellationPolicyHours: number;
  disableRescheduling?: boolean;
  disableLateCancellationRescheduling?: boolean;
  destinationCharges: boolean;
  disableEditProfileImage?: boolean;
  requireChargedBy?: boolean;
  country: string;
  platformTabs: {
    mainNav: PlatformTab[];
    trainerAccount: PlatformTab[];
    trainerDashboard: PlatformTab[];
    clientManagement: PlatformTab[];
    clientDashboard: PlatformTab[];
    clientAccount: PlatformTab[];
  };
  trainingInfoFields: TrainingInfoField[];
  statsList: MeasurementFieldConfig[];
  defaultHealthMetrics: string[];
  taxRates: TaxRate[];
  stripeAccountId: string;
  paymentMethodOrder: string[];
  newClientRequiredFields?: {
    id: string;
    name: string;
  }[];

  mainNavBgColor?: string;
  mainNavTextColor?: string;
  subNavBgColor?: string;
  subNavTextColor?: string;
  mainNavHighlightColor?: string;
  primaryButtonTextColor?: string;
  dropdownTextColor?: string;
  primaryButtonColor?: string;
  linkColor?: string;
  highlightColor?: string;
  supersetColor?: string;
  circuitColor?: string;
  supersetBorder?: string;
  circuitBorder?: string;
  appPrimaryColor?: string;
  measurementColors?: string;
  contactUrl?: string;
  website?: string;
  planTab?: string;
  customCss?: string;
  webAnalytics?: string;
  headWebAnalytics?: string;
  sendMonthlyEmails?: boolean;
  otpPlatform?: boolean;
  customSignup?: string;
  limitLoginAttempts?: boolean;
  useDarkMode?: boolean;
  metricPlatform?: boolean;
  hideCalendars?: boolean;
  showActivity?: boolean;
  disabledTabs?: string;
  allowsNonBilledClients?: boolean;
  showItemsOnThankYouPage?: boolean;
  showTrainingOnThankYouPage?: boolean;
  clientWelcome?: string;
  messagePlanPurchasers?: boolean;
  landingPageGroupOrder?: string;
  landingPageSectionOrder?: string;
  landingPagePlanOrder?: string;
  failedCheckout?: string;
  useFinderFilters?: boolean;
  landingRedirect?: string;
  welcomeNewUser?: string;
  firstWorkoutEmail?: boolean;
  alternateLanding?: string;
  sendingEmail?: string;
  sendingName?: string;
  healthInfoMetrics?: string[];
  measurementsOrder?: string[];
  defaultMeasurements?: string[];
  defaultMeasurementTags?: string[];
  requiredAssessment?: string;
  requiredSequence?: string;
  requireWaiver?: boolean;
  stripePlan?: string;
  clientLicensePlan?: string;
  defaultStripePlan?: string;
  trainerProfileFields?: string[];

  blockSitesToAddRoles?: boolean;
  blockSitesToEditRoles?: boolean;
  disableEditingLifecycleStages?: boolean;
  defaultChildPlatformRoles?: PlatformRole[];
  enableGuestCheckout?: boolean;
  allowUsersToSeeAccountBalance?: boolean;
  productPageForceFilters?: string[];
  macroCalculatorSettings?: MacroCalculatorSettings;
  disablePlatformSettingsTab?: boolean;
  disablePlatformSchedulingTab?: boolean;
  disablePlatformClientsTab?: boolean;
  subPlatformTabs?: {
    mainNav: PlatformTab[];
    trainerAccount: PlatformTab[];
    trainerDashboard: PlatformTab[];
    clientManagement: PlatformTab[];
    clientDashboard: PlatformTab[];
    clientAccount: PlatformTab[];
  };
  disableNavigationClientManagementTabs?: boolean;
  disableNavigationClientDashboardTabs?: boolean;
  disableNavigationClientAccountTabs?: boolean;
  disableNavigationTrainerDashboardTabs?: boolean;
  disableNavigationTrainerAccountTabs?: boolean;
  disableNavigationMainNavTabs?: boolean;
  disabledReports?: string[];

  // Feature Flag Settings
  supportedEntryTypes?: string[];
  defaultWorkoutType?: string;
  optInMailing?: boolean;
  disableLoggingCustomWorkouts?: boolean;
  disableEditingWorkouts?: boolean;
  disableLoggingFromUserPlans?: boolean;
  disableLoggingFromScheduledWorkouts?: boolean;
  disableLoggingFromFavoriteWorkouts?: boolean;
  disableLoggingFromRecentWorkouts?: boolean;
  showSetsInSummary?: boolean;
  makePurchasersClients?: boolean;
  makeAllAccountsClients?: boolean;
  hideAppCta?: boolean;
  useNavTextColorOnMobile?: boolean;
  trackCheckoutReferers?: boolean;
  disableAutostartTimer?: boolean;
  showToolbarTextLabels?: boolean;
  hideTotalTime?: boolean;
  hideTotalWeight?: boolean;
  hideTotalDistance?: boolean;
  hideTotalCalories?: boolean;
  requireBarcodesForCheckin?: boolean;
  allowCommissionWithFreePackages?: boolean;

  // Kiosk Check-in Settings
  allowCheckIn?: boolean;
  checkInPayment?: boolean;
  checkInServices?: boolean;
  checkInAssessment?: string | null;

  theme: {
    primary: ColorHex;
    secondary: ColorHex;
    neutral: ColorHex;
    forcedTheme: 'light' | 'dark' | null;
  };

  webTheme: {
    primary: ColorHex;
    linkColor: ColorHex;
    highlightColor: ColorHex;
    subNavBgColor: ColorHex;
    subNavTextColor: ColorHex;
    mainNavBgColor: ColorHex;
    mainNavTextColor: ColorHex;
    mainNavHighlightColor: ColorHex;
    primaryButtonColor: ColorHex;
    primaryButtonTextColor: ColorHex;
    dropdownTextColor: ColorHex;
    supersetColor: ColorHex;
    supersetBorder: ColorHex;
    circuitBorder: ColorHex;
    mainChromeTextColor: ColorHex;
    blockBackgroundColor: ColorHex;
    supersetBackgroundColor: ColorHex;
    circuitBackgroundColor: ColorHex;
  };

  // admin fields
  currentIosVersion: string;
  currentAndroidVersion: string;
  enableFbm: boolean;
  waiver: string;
  waiverFields: { type: string; label: string }[];
  nutritionNotesTemplate: string;
  defaultCurrency: string;
  doNotDeductLateCancel: boolean;
  useStripeExpress: boolean;
  otherPaymentMethodLabels: string[];
  connectedTo: string[];
  iosArn: string;
  androidArn: string;
  pushCertificateExpiresAt: string;

  // White Label settings
  contactPhone?: string;
  mailingAddress?: string;
  hoursOfOperation?: string;
  supportEmailAddress?: string;
  mailingList?: string;
  maxWeeks?: string;
  maxLocations?: string;
  platformType?: string;
  parentId?: string | number;
  enableHq?: boolean;
  emailPreferencesLink?: string;
  viewAllTrainerMessages?: boolean;
  disableEmailExceptions?: string[];
  trainersToMessage?: Identifier[];
  disableRepMax?: boolean;
  autoFillWorkoutWeights?: boolean;
  noCcForTrials?: boolean;
  cancelTrialOnPaymentFailure?: boolean;
  subscriptionInvoiceRetryBehavior?: string;
  hideProfileStats?: boolean;
  hideBlockTitles?: boolean;
  hideMyPerformance?: boolean;
  thankYouPlans?: string[];
  thankYouGroups?: string[];
  hideClientLanding?: boolean;
  landingGroups?: string[];
  landingProducts?: string[];
  landingWorkoutPlans?: string[];
  landingPackages?: string[];
  packageLandingTags?: string[];
  publicExerciseTags?: string[];
  hideExerciseFilters?: string[];
  freeUserGroup?: string;
  useLongCheckout?: boolean;
  darkModeForCheckout?: boolean;
  requiredWaiverAppText?: string;
  customTerms?: string;
  requireCustomTerms?: boolean;
  reasonsForCancellation?: { label: string }[];
  cancellationMessage?: string;
  showOtherPlansOnIndexPage?: string;
  showCollectionScroller?: boolean;
  showScheduleTime?: string;
  excludeFromFbm?: Identifier[];
  freeNoshowVisits?: string;
  freeClassPackage?: boolean;
  freeClasses?: string;
  excludeNoShowsFromLimitBooking?: boolean;
  excludeLateCancellationsFromLimitBooking?: boolean;
  firstDayOfWeek?: number;
  requireTrainerForBooking?: boolean;
  serviceBalanceNoPrepayment?: boolean;
  bookingCalendar?: boolean;
  hideSpotsRemaining?: boolean;
  forceDependent?: boolean;
  bookPackageSessions?: boolean;
  useUserLocation?: boolean;
  showFullSessions?: boolean;
  showClosedSessions?: boolean;
  bookingFilters?: string[];
  bookingAvailableDefault?: string;
  forceBookingFilters?: string[];
  servicesOrder?: string[];
  staffOrder?: string[];
  hideTrainersFromBooking?: number[];
  packageFilters?: string[];
  forcePackageFilters?: string[];
  finderItems?: { itemId: number; itemType: 'product' | 'package' | 'plan' | 'group' }[];
  finderItemTypes?: { id: 'all' | 'product' | 'package' | 'plan' | 'group'; name?: string; hidden?: boolean }[];
  checkoutTags?: string[];
  limitBooking?: boolean;
  limitBookingDays?: string;
  limitBookingFutureDays?: string;
  limitBookingServices?: LimitBookingService[];
  gymCheckIn?: boolean;
  openGymServiceId?: number;
  checkInSearch?: string;
  onlineTrainingDestinationType?: string;
  onlineTrainingDestinationId?: string;
  clientThankYouMessage?: string;
  clientThankYouPage?: string;
  hideAlerts?: string[];
  redAlerts?: string[];
  useSubtrainerAsSender?: boolean;
  doubleEachSideWorkoutWeights?: boolean;

  // invoice settings
  businessProfile: {
    name: string | null;
    supportAddress: {
      city: string | null;
      state: string | null;
      postalCode: string | null;
      country: string | null;
      line1: string | null;
      line2: string | null;
    };
    supportEmail: string | null;
    supportPhone: string | null;
    url: string | null;
    supportUrl: string | null;
  };
  receiptFooter: string | null;
  receiptCustomFields: ReceiptCustomField[];

  // terminal stuff
  stripeLocationId: string;
  stripeLocationIds: string[];
  appleApiKey?: string | null;

  // apps stuff
  managedAppsNumber?: string;
  appBundleId?: string;
  appAndroidBundleId?: string;
  appAppleTeamId?: string;
  appFirebaseId?: string;
  appItcTeamId?: string;
  appItcTeamName?: string;
  appItcTeamDisplayName?: string;
  appItcAppPassword?: string;
  appProductAppId?: string;
  appShortName?: string;
  appLongName?: string;
  appDescription?: string;
  appAppleId?: string;
  iosDownloadLink?: string;
  androidDownloadLink?: string;
  unpublishedAt?: Timestamp;
  rusinVideosImportedAt?: Timestamp;
  twilio10dlcStatus?: string;
  twilio10DlcData?: any;
  twilio10dlcExcluded?: boolean;
  serviceAccountKey?: string;
  intercomAppId?: string;

  // app customization
  profileMenu?: ProfileMenuItem[];
  customAppMenu?: AppDrawerMenuItem[];
  customAppTabs?: AppNavigationTab[];
  termsOfServiceUrl?: string;
  privacyPolicyUrl?: string;

  platformRoles?: PlatformRole[];

  // platform node id
  fbmPlatformNodeId?: string;

  gtmContainerId?: string;

  // video tag sort
  videoTagSort?: string;
  videoTaggedVideoSort?: string;

  // Fee Settings
  transactionFeePer?: number;
  transactionFeeAmount?: number;
  fbmTransactionFeePer?: number;
  fbmTransactionFeeAmount?: number;
  invoiceFeePer?: number;
  invoiceFeeAmount?: number;

  // Bank Transfer Fees
  achTransactionFeePer?: number;
  achTransactionFeeAmount?: number;
  achTransactionCap?: number;
  auBecsDebitFeePer?: number;
  auBecsDebitFeeAmount?: number;
  auBecsDebitCap?: number;
  bacsDebitFeePer?: number;
  bacsDebitFeeAmount?: number;
  bacsDebitCap?: number;
  acssDebitFeePer?: number;
  acssDebitFeeAmount?: number;
  acssDebitCap?: number;
  sepaDebitFeePer?: number;
  sepaDebitFeeAmount?: number;
  sepaDebitCap?: number;

  // BNPL Fees
  klarnaEcommerceFeePer?: number;
  klarnaEcommerceFeeAmount?: number;
  klarnaEcommerceCap?: number;
  klarnaFbmFeePer?: number;
  klarnaFbmFeeAmount?: number;
  klarnaFbmCap?: number;
  afterpayClearpayFeePer?: number;
  afterpayClearpayFeeAmount?: number;
  afterpayClearpayCap?: number;

  // Stripe Fees
  stripeCardFeePer?: number;
  stripeCardFeeAmount?: number;
  stripeInvoiceFeePer?: number;
  stripeInvoiceFeeAmount?: number;
  stripeSepaDebitFeeCap?: number;
  stripeSepaDebitFeePer?: number;
  stripeSepaDebitFeeAmount?: number;
  stripeAchFeeCap?: number;
  stripeAchFeePer?: number;
  stripeAchFeeAmount?: number;
  stripeBacsDebitFeeCap?: number;
  stripeBacsDebitFeePer?: number;
  stripeBacsDebitFeeAmount?: number;
  stripeAuBecsDebitFeeCap?: number;
  stripeAuBecsDebitFeePer?: number;
  stripeAuBecsDebitFeeAmount?: number;
  stripeAcssDebitFeeCap?: number;
  stripeAcssDebitFeePer?: number;
  stripeAcssDebitFeeAmount?: number;
  stripeKlarnaFeePer?: number;
  stripeKlarnaFeeAmount?: number;
  stripeAfterpayClearpayPer?: number;
  stripeAfterpayClearpayAmount?: number;

  // Stripe Tax
  stripeTaxCalculationPer?: number;
  stripeTaxTransactionPer?: number;
  stripeTaxSubscriptionPer?: number;
  stripeTaxFixedInvoiceFee?: number;

  // SMS Pricing
  smsPricePer?: number;
  smsFlatPrice?: number;
  smsFreeCount?: number;
  doNotTruncateSms?: boolean;
  hideSmsDefaultSessionPrompt?: boolean;

  // Client Pricing
  clientCount?: number;
  pricePerClient?: number;
  locationStripePlan?: string;

  // App Dashboard
  appDashboard?: {
    list: {
      style: number;
    };
    carousel: {
      title: string;
      items: AppDashboardCarouselItem[];
    };
  };

  // In-App Purchases
  iap?: IAPItem[];

  // App Logger Navigation
  appLoggerNav?: AppLoggerNavItem[];
  appLoggerWorkoutNav?: AppLoggerNavItem[];

  // Domain Verification
  domainVerification?: {
    domain: string;
    verified: boolean;
    txtRecords?: string[];
  };

  // Images
  shareWorkoutImageUrl?: string;

  // Refer a Friend
  referAFriend?: ReferAFriendSettings;
  referAFriendImageUrl?: string;
  referAFriendImage?: string; // signed_id for upload
}

export interface PlatformRole {
  id: Identifier;
  name: string;
  permissions: RoleProps & {
    disabledTabs: string[];
    disabledDashboardBoxes: string[];
    disabledEmails: string[];
    disabledDashboardNotifications: string[];
    headTrainerEmails: string[];
    reportsRole: string[];
  };
}

export interface ReceiptCustomField {
  name: string;
  value: string;
}

export interface PlatformTab {
  id: string;
  name: string;
  route: string;
  enabled: boolean;
}

export interface StripeAccount {
  type: string;
  stripe_account_id: string;
  name: string;
}

export interface TenDlcError {
  type: 'ein_mismatch' | 'invalid_ein' | 'otp_pending';
  detectedAt: string;
  notificationCount: number;
  lastNotifiedAt?: string;
  details?: {
    failureReason?: string;
  };
}

export interface TenDlcDetails {
  twilio10dlcStatus: string;
  twilioMessagingService: string;
  twilio10dlcError?: TenDlcError | null;
  daysUntilSmsDisabled?: number | null;
}

export interface TenDlcCsv {
  company: string;
  website: string;
  businessType: string;
  businessIdType: string;
  businessId: string;
  regions: string;
  jobTitle: string;
  jobPosition: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  areaCode: string;
  areaCodeTwo: string;
}

export interface TaxRate {
  ach: boolean;
  card: boolean;
  ecommerce: boolean;
  fbm: boolean;
  id: number;
  label: string;
  percentage: number;
}

export interface Balance {
  object: string;
  available: {
    amount: number;
    currency: string;
    source_types: {
      bank_account: number;
      card: number;
    };
  }[];
  instant_available: {
    amount: number;
    currency: string;
    source_types: {
      bank_account: number;
      card: number;
    };
  }[];
  livemode: boolean;
  pending: {
    amount: number;
    currency: string;
    source_types: {
      bank_account: number;
      card: number;
    };
  }[];
}

export interface Transfer {
  id: number;
  amount: number;
  location: string;
  memo: string;
  createdAt: string;
  updatedAt: string;
}

export interface TransferRequest {
  destination: string;
  amount: number;
  memo: string;
  type: 'location' | 'trainer';
}

// Types for Formik errors
export interface ProfileMenuItemFormikErrors {
  profileMenu?: Array<FormikErrors<ProfileMenuItem>>;
}

export interface ProfileMenuItemFormikTouched {
  profileMenu?: Array<FormikTouched<ProfileMenuItem>>;
}

export interface AppDrawerMenuItemFormikErrors {
  drawerMenu?: Array<FormikErrors<AppDrawerMenuItem>>;
}

export interface AppDrawerMenuItemFormikTouched {
  drawerMenu?: Array<FormikTouched<AppDrawerMenuItem>>;
}

export interface AppNavigationTabFormikErrors {
  navigationTabs?: Array<FormikErrors<AppNavigationTab>>;
}

export interface AppNavigationTabFormikTouched {
  navigationTabs?: Array<FormikTouched<AppNavigationTab>>;
}

export interface BuildLog {
  id: string;
  platformId: number;
  triggeringUserId: number;
  triggeringUserName: string;
  repository: string;
  triggeredRef: string;
  platformType: 'ios' | 'android';
  ciProvider: 'github' | 'gitlab' | null;
  buildNumber: number;
  inputs: Record<string, any>;
  ciRunId: number | null;
  commitSha: string | null;
  appVersion: string | null;
  status: 'pending' | 'building' | 'complete' | 'error';
  statusMessage: string | null;
  ciStatus: string | null;
  ciConclusion: string | null;
  ciHtmlUrl: string | null;
  artifacts: Record<string, string> | null;
  createdAt: string;
  updatedAt: string;
}

export interface MacroCalculatorSettings {
  bmrFormula: 'harris_benedict' | 'mifflin_st_jeor';
  activityMultipliers: {
    sedentary: number;
    light: number;
    moderate: number;
    veryActive: number;
    extraActive: number;
  };
  macroMethod:
    | 'percentage_based'
    | 'body_weight_based'
    | 'hybrid_protein_bodyweight_fat_percentage';
  macroPercentages: {
    protein: number;
    fat: number;
    carbs: number;
  };
  macroPerKg: {
    protein: number;
    fat: number;
  };
  goalAdjustments: {
    lose: number;
    maintain: number;
    gain: number;
  };
  calorieLimits: {
    femaleMinimum: number;
    maleMinimum: number;
    maximum: number;
  };
  rounding: {
    calories: 'whole' | 'decimal';
    macros: 'whole' | 'oneDecimal';
  };
  customDescription?: string;
  customResultsInfo?: string;
}

export interface InviteLink {
  id: Identifier;
  platformId: Identifier;
  createdById: Identifier;
  redeemedById: Identifier;
  inviteItemId: Identifier;
  inviteItemType: string;
  expiresAt: Timestamp;
  redeemedAt: Timestamp;
  link: string;
}

export interface HideWorkoutSchedules {
  id: Identifier;
  platformId: Identifier;
  name: string;
  startDate: Timestamp;
  endDate: Timestamp;
  trainerIds: Identifier[];
  description?: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface RequireLocationResponse {
  requireLocation: boolean;
}
